import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import BreadCrumb from '../../../components/BreadCrumb';
import NoDataFound from '../../../components/common/NoDataFound';
import { RequestCard } from './components/RequestCard';
import { UmpireRequestCard } from './components/UmpireRequestCard';
import { ScorerRequestCard } from './components/ScorerRequestCard';
import { AllPlayersCard } from './components/AllPlayersCard';
import { AllPlayersListView } from './components/AllPlayersListView';
import { PlayerRequestsListView } from './components/PlayerRequestsListView';
import { StaffRequestsListView } from './components/StaffRequestsListView';
import { ClaimProfileCard } from './components/ClaimProfileCard';
import { AddRoleModal } from './components/AddRoleModal';
import {
  fetchJoinLeagueRequests,
  bulkChangeUmpireStatus,
  bulkChangeScorerStatus,
  bulkChangePlayerStatus,
  assignRoleToPlayerInLeague,
  claimPlayerInLeague,
  approvePlayerClaim,
  rejectPlayerClaim,
  type LeaguePermissionRole,
} from './api/requestApi';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeagueId, selectSelectedLeague } from '../../../redux/slice/selectedLeagueSlice';
import type {
  IJoinLeagueRequest,
  IUmpireRequest,
  IScorerRequest,
  IPlayerByLeagueForRequest,
  IProfileClaim,
} from './types/request';
import './Request.scss';
import { Images } from '../../../utils/imageConstants';

type RequestTab = 'player' | 'umpire' | 'scorer' | 'allPlayers' | 'claim';

const Request: React.FC = () => {
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canAddPlayer = selectedLeague?.canAddPlayerToTeam !== false;
  const [activeSubTab, setActiveSubTab] = useState<RequestTab>('allPlayers');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [allPlayersSearch, setAllPlayersSearch] = useState('');
  const [addRolePlayer, setAddRolePlayer] = useState<IPlayerByLeagueForRequest | null>(null);
  const [addRoleSubmitting, setAddRoleSubmitting] = useState(false);
  const [mergingPlayerId, setMergingPlayerId] = useState<string | null>(null);
  const [claimActionSubmittingId, setClaimActionSubmittingId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      QUERY_KEY.getJoinLeagueRequests,
      QUERY_KEY.getUmpireRequestsForLeague,
      QUERY_KEY.getScorerRequestsForLeague,
      QUERY_KEY.getAllPlayersByLeague,
      QUERY_KEY.getProfileClaimsByLeagueId,
      selectedLeagueId,
    ],
    enabled: !!selectedLeagueId,
    retry: false,
    queryFn: async () => {
      if (!selectedLeagueId) {
        return {
          playerRequests: [],
          umpireRequests: [],
          scorerRequests: [],
          allPlayers: [],
          profileClaims: [],
        };
      }
      return fetchJoinLeagueRequests(selectedLeagueId);
    },
  });

  const playerRequests = data?.playerRequests ?? [];
  const umpireRequests = data?.umpireRequests ?? [];
  const scorerRequests = data?.scorerRequests ?? [];
  const allPlayers = data?.allPlayers ?? [];
  const profileClaims = data?.profileClaims ?? [];

  const filteredAllPlayers = useMemo(() => {
    const query = allPlayersSearch.trim().toLowerCase();
    if (!query) return allPlayers;

    return allPlayers.filter((player) => {
      const fullName = [player.playerFirstName, player.playerLastName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const playerId = (player.playerId ?? '').toLowerCase();
      const teamCode = (player.teamCode ?? '').toLowerCase();
      const teamSnapshotId = (player.teamSnapshotId ?? '').toLowerCase();
      const playerRole = (player.playerRole ?? '').toLowerCase();
      const battingStyle = (player.battingStyle ?? '').toLowerCase();
      const bowlingStyle = (player.bowlingStyle ?? '').toLowerCase();

      return (
        fullName.includes(query) ||
        playerId.includes(query) ||
        teamCode.includes(query) ||
        teamSnapshotId.includes(query) ||
        playerRole.includes(query) ||
        battingStyle.includes(query) ||
        bowlingStyle.includes(query)
      );
    });
  }, [allPlayers, allPlayersSearch]);

  const requestsByTab: Record<
    RequestTab,
    (IJoinLeagueRequest | IUmpireRequest | IScorerRequest | IPlayerByLeagueForRequest | IProfileClaim)[]
  > = {
    player: playerRequests,
    umpire: umpireRequests,
    scorer: scorerRequests,
    allPlayers: canAddPlayer ? filteredAllPlayers : [],
    claim: profileClaims,
  };

  const currentRequests = requestsByTab[activeSubTab];
  const hasSelectableItems =
    activeSubTab === 'player' ||
    activeSubTab === 'umpire' ||
    activeSubTab === 'scorer' ||
    activeSubTab === 'claim';
  const showAcceptReject = hasSelectableItems;

  const getRequestId = (
    r:
      | IJoinLeagueRequest
      | IUmpireRequest
      | IScorerRequest
      | IPlayerByLeagueForRequest
      | IProfileClaim,
    tab: RequestTab
  ): string => {
    if (tab === 'umpire') {
      const ur = r as IUmpireRequest;
      return ur.umpireId ?? ur.name ?? '';
    }
    if (tab === 'scorer') {
      const sr = r as IScorerRequest;
      return sr.scorerId ?? sr.name ?? '';
    }
    if (tab === 'allPlayers') {
      const p = r as IPlayerByLeagueForRequest;
      return p.playerId ?? `${p.playerFirstName ?? ''}-${p.playerLastName ?? ''}`;
    }
    if (tab === 'claim') {
      const c = r as IProfileClaim;
      const unClaimed = c.unClaimedPlayer ?? c.profile1;
      const claimed = c.claimedPlayer ?? c.profile2;
      const n1 = [unClaimed?.playerFirstName, unClaimed?.playerLastName].filter(Boolean).join(' ') || '';
      const n2 = [claimed?.playerFirstName, claimed?.playerLastName].filter(Boolean).join(' ') || '';
      return c.claimId ?? unClaimed?.playerId ?? `${n1}-${n2}`;
    }
    const jr = r as IJoinLeagueRequest;
    return (
      jr.playerId ??
      jr.teamSnapshotId ??
      jr.teamId ??
      `${jr.teamName ?? ''}-${jr.tournamentId ?? ''}`
    );
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentRequests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentRequests.map((r) => getRequestId(r, activeSubTab))));
    }
  };

  const runBulkStatus = async (action: 'accept' | 'reject') => {
    if (!selectedLeagueId || selectedIds.size === 0) return;

    setBulkSubmitting(true);
    try {
      if (activeSubTab === 'umpire') {
        const rows = umpireRequests.filter((r) => selectedIds.has(getRequestId(r, 'umpire')));
        const missing = rows.filter((r) => !(r.playerId ?? r.umpireId)?.trim());
        if (missing.length) {
          toast.error('Some selected umpires are missing player/umpire id.');
          return;
        }
        const entries = rows.map((r) => ({
          playerId: (r.playerId ?? r.umpireId)!.trim(),
          requestStatus: action === 'accept' ? ('ACCEPT' as const) : ('REJECT' as const),
        }));
        const msg = await bulkChangeUmpireStatus(selectedLeagueId, entries);
        if (msg === 'Empty List') {
          toast('Nothing to update.', { icon: '⚠️' });
        } else {
          toast.success(action === 'accept' ? 'Umpire request(s) accepted.' : 'Umpire request(s) rejected.');
        }
      } else if (activeSubTab === 'scorer') {
        const rows = scorerRequests.filter((r) => selectedIds.has(getRequestId(r, 'scorer')));
        const missing = rows.filter((r) => !(r.playerId ?? r.scorerId)?.trim());
        if (missing.length) {
          toast.error('Some selected scorers are missing player/scorer id.');
          return;
        }
        const entries = rows.map((r) => ({
          playerId: (r.playerId ?? r.scorerId)!.trim(),
          requestStatus: action === 'accept' ? ('ACCEPT' as const) : ('REJECT' as const),
        }));
        const msg = await bulkChangeScorerStatus(selectedLeagueId, entries);
        if (msg === 'Empty List') {
          toast('Nothing to update.', { icon: '⚠️' });
        } else {
          toast.success(action === 'accept' ? 'Scorer request(s) accepted.' : 'Scorer request(s) rejected.');
        }
      } else if (activeSubTab === 'player') {
        const rows = playerRequests.filter((r) => selectedIds.has(getRequestId(r, 'player')));
        const missing = rows.filter((r) => !(r as IJoinLeagueRequest).playerId?.trim());
        if (missing.length) {
          toast.error(
            'Some selected rows have no playerId. The server must return playerId on join requests for bulk update.'
          );
          return;
        }
        const playerIds = rows.map((r) => (r as IJoinLeagueRequest).playerId!.trim());
        const msg = await bulkChangePlayerStatus(
          selectedLeagueId,
          playerIds,
          action === 'accept' ? 'APPROVE' : 'REJECT'
        );
        if (msg === 'Empty List') {
          toast('Nothing to update.', { icon: '⚠️' });
        } else {
          toast.success(
            action === 'accept' ? 'Player request(s) approved.' : 'Player request(s) rejected.'
          );
        }
      } else if (activeSubTab === 'claim') {
        const rows = profileClaims.filter((c) => selectedIds.has(getRequestId(c, 'claim')));
        const missing = rows.filter((c) => {
          const unClaimedId = (c.unClaimedPlayer ?? c.profile1)?.playerId;
          const requestPlayerId = (c.claimedPlayer ?? c.profile2)?.playerId;
          return !unClaimedId || !requestPlayerId;
        });
        if (missing.length) {
          toast.error('Some selected claims are missing player ids.');
          return;
        }
        const results = await Promise.allSettled(
          rows.map((c) => {
            const unClaimedId = (c.unClaimedPlayer ?? c.profile1)!.playerId!;
            const requestPlayerId = (c.claimedPlayer ?? c.profile2)!.playerId!;
            return action === 'accept'
              ? approvePlayerClaim(selectedLeagueId, unClaimedId, requestPlayerId)
              : rejectPlayerClaim(selectedLeagueId, unClaimedId, requestPlayerId);
          })
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          toast.error(
            `${failed} of ${rows.length} claim(s) failed to ${action === 'accept' ? 'approve' : 'reject'}.`
          );
        } else {
          toast.success(action === 'accept' ? 'Claim(s) approved.' : 'Claim(s) rejected.');
        }
      } else {
        return;
      }

      setSelectedIds(new Set());
      await refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message ?? 'Request failed. Please try again.';
      toast.error(String(message));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleAccept = () => {
    void runBulkStatus('accept');
  };

  const handleReject = () => {
    void runBulkStatus('reject');
  };

  const handleAssignRole = async (roleName: LeaguePermissionRole) => {
    const playerId = addRolePlayer?.playerId;
    if (!selectedLeagueId || !playerId) {
      toast.error('Missing player or league id.');
      return;
    }
    setAddRoleSubmitting(true);
    try {
      await assignRoleToPlayerInLeague(selectedLeagueId, playerId, roleName);
      toast.success('Role assigned successfully.');
      setAddRolePlayer(null);
      await refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message ?? 'Failed to assign role. Please try again.';
      toast.error(String(message));
    } finally {
      setAddRoleSubmitting(false);
    }
  };

  const handleMerge = async (player: IPlayerByLeagueForRequest) => {
    const playerId = player.playerId;
    if (!selectedLeagueId || !playerId) {
      toast.error('Missing player or league id.');
      return;
    }
    setMergingPlayerId(playerId);
    try {
      await claimPlayerInLeague(selectedLeagueId, playerId);
      toast.success('Merge request submitted.');
      await refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message ?? 'Failed to submit merge request. Please try again.';
      toast.error(String(message));
    } finally {
      setMergingPlayerId(null);
    }
  };

  const handleClaimAction = async (claim: IProfileClaim, action: 'approve' | 'reject') => {
    const unClaimedId = (claim.unClaimedPlayer ?? claim.profile1)?.playerId;
    const requestPlayerId = (claim.claimedPlayer ?? claim.profile2)?.playerId;
    if (!selectedLeagueId || !unClaimedId || !requestPlayerId) {
      toast.error('Missing claim player ids.');
      return;
    }
    const rowKey = claim.claimId ?? unClaimedId;
    setClaimActionSubmittingId(rowKey);
    try {
      if (action === 'approve') {
        await approvePlayerClaim(selectedLeagueId, unClaimedId, requestPlayerId);
        toast.success('Claim approved.');
      } else {
        await rejectPlayerClaim(selectedLeagueId, unClaimedId, requestPlayerId);
        toast.success('Claim rejected.');
      }
      await refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message ?? 'Failed to update claim. Please try again.';
      toast.error(String(message));
    } finally {
      setClaimActionSubmittingId(null);
    }
  };

  const showRequestViewToggle =
    activeSubTab === 'allPlayers' ||
    activeSubTab === 'player' ||
    activeSubTab === 'umpire' ||
    activeSubTab === 'scorer';

  const subTabs: { id: RequestTab; label: string }[] = [
    { id: 'player', label: 'Player' },
    { id: 'umpire', label: 'Umpire' },
    { id: 'scorer', label: 'Scorer' },
    { id: 'allPlayers', label: 'All Players' },
    { id: 'claim', label: 'Claim' },
  ];

  return (
    <div className="main-middle-content-wrap request-page-wrap">
      <div className="request-wrapper custom-container">
        <BreadCrumb />
        {/* {activeSubTab !== 'claim' && (
          <h1 className="request-heading">
            {activeSubTab === 'allPlayers' && 'All Players'}
            {activeSubTab === 'player' && 'Player Requests'}
            {activeSubTab === 'umpire' && 'Umpire Requests'}
            {activeSubTab === 'scorer' && 'Scorer Requests'}
          </h1>
        )} */}

        <div className={`request-content-card ${activeSubTab === 'allPlayers' ? 'request-content-card-all-players' : ''} ${activeSubTab === 'claim' ? 'request-content-card-claim' : ''}`}>
          <div className="tournaments-tab-wrapper">
            <div className="tournaments-tab-scroll" role="tablist" aria-label="Request categories">
              {subTabs.map((tab) => {
                const isActive = activeSubTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`tab ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      setActiveSubTab(tab.id);
                      setSelectedIds(new Set());
                      if (tab.id !== 'allPlayers') {
                        setAllPlayersSearch('');
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {showRequestViewToggle && (
              <div className="right-icons">
                <div className="request-all-players-search">
                  <img src={Images.dark_search_icon} alt="" aria-hidden />
                  <input
                    type="search"
                    value={allPlayersSearch}
                    onChange={(e) => setAllPlayersSearch(e.target.value)}
                    placeholder="Search Players..."
                    aria-label="Search all players"
                    autoComplete="off"
                  />
                </div>
                <div className="request-view-switch" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`tournaments-view-toggle ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    aria-label="Grid view"
                    aria-pressed={viewMode === 'grid'}
                  >
                    <img src={Images.card_show_icon} alt="" />
                  </button>
                  <button
                    type="button"
                    className={`tournaments-view-toggle ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    aria-label="List view"
                    aria-pressed={viewMode === 'list'}
                  >
                    <img src={Images.list_view_icon} alt="" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {activeSubTab === 'claim' && (
            <div className="request-claim-header">
              <h2 className="request-claim-title">Claim Profile</h2>
              <label className="request-select-all">
                <input
                  type="checkbox"
                  checked={
                    profileClaims.length > 0 &&
                    selectedIds.size === profileClaims.length
                  }
                  onChange={toggleSelectAll}
                />
                Select All
              </label>
            </div>
          )}

          {activeSubTab === 'allPlayers' && (
            <div className="request-all-players-header">
              <h2 className="request-all-players-title">All Players</h2>

            </div>
          )}

          {currentRequests.length > 0 && !(activeSubTab === 'allPlayers') && activeSubTab !== 'claim' && hasSelectableItems && (
            <div className="request-top-controls">
              <label className="request-select-all">
                <input
                  type="checkbox"
                  checked={
                    currentRequests.length > 0 &&
                    selectedIds.size === currentRequests.length
                  }
                  onChange={toggleSelectAll}
                />
                Select All
              </label>
            </div>
          )}

          {isLoading ? (
            <div className="request-loading">Loading...</div>
          ) : isError ? (
            <div className="request-error">
              <p>Unable to load requests. The server may not support this feature yet (500 error).</p>
              <button type="button" className="request-btn-retry" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : activeSubTab === 'allPlayers' && !canAddPlayer ? (
            <NoDataFound message="Player management is not available for this league." />
          ) : !currentRequests || currentRequests.length === 0 ? (
            <NoDataFound message="No requests found" />
          ) : (
            <>
              {activeSubTab === 'allPlayers' && viewMode === 'list' ? (
                <AllPlayersListView
                  players={filteredAllPlayers}
                  onAddRole={setAddRolePlayer}
                  onMerge={handleMerge}
                  mergingPlayerId={mergingPlayerId}
                />
              ) : activeSubTab === 'player' && viewMode === 'list' ? (
                <PlayerRequestsListView
                  requests={playerRequests}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  getRequestId={(r) => getRequestId(r, 'player')}
                />
              ) : activeSubTab === 'umpire' && viewMode === 'list' ? (
                <StaffRequestsListView
                  requests={umpireRequests}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  getRequestId={(r) => getRequestId(r, 'umpire')}
                  columnHeader="UMPIRE"
                />
              ) : activeSubTab === 'scorer' && viewMode === 'list' ? (
                <StaffRequestsListView
                  requests={scorerRequests}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  getRequestId={(r) => getRequestId(r, 'scorer')}
                  columnHeader="SCORER"
                />
              ) : (
                <div
                  className={`request-cards ${viewMode === 'list' ? 'request-cards-list' : 'request-cards-grid'
                    } ${activeSubTab === 'player' ? 'request-cards-player' : ''} ${activeSubTab === 'umpire' ? 'request-cards-umpire' : ''} ${activeSubTab === 'scorer' ? 'request-cards-scorer' : ''} ${activeSubTab === 'allPlayers' ? 'request-cards-allplayers' : ''} ${activeSubTab === 'claim' ? 'request-cards-claim' : ''}`}
                >
                  {currentRequests.map((req) => {
                    const id = getRequestId(req, activeSubTab);
                    if (activeSubTab === 'allPlayers') {
                      const player = req as IPlayerByLeagueForRequest;
                      return (
                        <AllPlayersCard
                          key={id}
                          player={player}
                          onAddRole={setAddRolePlayer}
                          onMerge={handleMerge}
                          mergeSubmitting={!!player.playerId && mergingPlayerId === player.playerId}
                        />
                      );
                    }
                    if (activeSubTab === 'claim') {
                      const claim = req as IProfileClaim;
                      const claimKey = claim.claimId ?? (claim.unClaimedPlayer ?? claim.profile1)?.playerId ?? id;
                      return (
                        <ClaimProfileCard
                          key={id}
                          claim={claim}
                          selected={selectedIds.has(id)}
                          onToggleSelect={() => toggleSelect(id)}
                          onApprove={() => handleClaimAction(claim, 'approve')}
                          onReject={() => handleClaimAction(claim, 'reject')}
                          actionSubmitting={claimActionSubmittingId === claimKey}
                        />
                      );
                    }
                    if (activeSubTab === 'umpire') {
                      return (
                        <UmpireRequestCard
                          key={id}
                          request={req as IUmpireRequest}
                          selected={selectedIds.has(id)}
                          onToggleSelect={() => toggleSelect(id)}
                        />
                      );
                    }
                    if (activeSubTab === 'scorer') {
                      return (
                        <ScorerRequestCard
                          key={id}
                          request={req as IScorerRequest}
                          selected={selectedIds.has(id)}
                          onToggleSelect={() => toggleSelect(id)}
                        />
                      );
                    }
                    return (
                      <RequestCard
                        key={id}
                        request={req as IJoinLeagueRequest}
                        selected={selectedIds.has(id)}
                        onToggleSelect={() => toggleSelect(id)}
                      />
                    );
                  })}
                </div>
              )}
              {showAcceptReject && (
                <div className="request-actions">
                  <button
                    type="button"
                    className="request-btn-accept"
                    onClick={handleAccept}
                    disabled={selectedIds.size === 0 || bulkSubmitting}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Accept
                  </button>
                  <button
                    type="button"
                    className="request-btn-reject"
                    onClick={handleReject}
                    disabled={selectedIds.size === 0 || bulkSubmitting}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Reject
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddRoleModal
        open={!!addRolePlayer}
        onOpenChange={(open) => {
          if (!open) setAddRolePlayer(null);
        }}
        playerName={
          [addRolePlayer?.playerFirstName, addRolePlayer?.playerLastName]
            .filter(Boolean)
            .join(' ') || 'Player'
        }
        submitting={addRoleSubmitting}
        onAssign={handleAssignRole}
      />
    </div>
  );
};

export default Request;
